import React, { useMemo, useRef, useState } from 'react';
import { IBuildingBlock } from '../../types/@types/generated/contentful';
import { breakpoints, colors, spacing } from '../../utils/styleguide';
import ExpandableBox from '../component-library/expandableBox/ExpandableBox';
import Typography from '../component-library/text/Typography';
import { PageSectionComponent } from './pageSections';
import RichText from './RichText/RichText';
import styled from '@emotion/styled';
import Icon from '../component-library/Icon';
import { Col, Row } from '../component-library/layout/12ColGridLayout';
import { Document } from '@contentful/rich-text-types';

const MainWrapper = styled(Row)`
  position: relative;

  // Negative values is due to removing the top-margin of respective item (to allow all items to be optional)
  margin-top: ${spacing[8] - spacing[6]}px;
  margin-bottom: ${spacing[8]}px;

  ${breakpoints.tablet} {
    margin-top: ${spacing[10] - spacing[6]}px;
    margin-bottom: ${spacing[10]}px;
  }
`;

const Tag = styled.div`
  height: 36px;
  padding: 4px 20px;
  border-radius: 40px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  margin-right: ${spacing[1]}px;
  margin-bottom: ${spacing[1]}px;
  white-space: nowrap;
`;

const CategoryWrapper = styled.div`
  margin-top: ${spacing[6]}px;
  display: flex;
  overflow-y: scroll;

  ${breakpoints.tablet} {
    flex-wrap: wrap;
    overflow-y: auto;
  }
`;

const MoreArrow = styled.div<{ isScrollable: boolean }>`
  position: absolute;
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
  right: 0;
  height: 40px;
  width: 50px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, ${colors.white} 50%);

  ${breakpoints.tablet} {
    display: none;
  }

  ${({ isScrollable }) => (isScrollable ? '' : 'display: none;')}
`;

const QuestionAndAnswer = styled(ExpandableBox)`
  margin-top: ${spacing[3]}px;

  :first-child {
    margin-top: 0;
  }
`;

const FooterWrapper = styled.div`
  margin-top: ${spacing[6]}px;

  ${breakpoints.tablet} {
    margin-top: ${spacing[10]}px;
  }
`;

function setFirstCharToUpperCase(str: string) {
  return `${str[0].toUpperCase()}${str.slice(1)}`;
}

const QuestionsAndAnswersSection: PageSectionComponent<null> = ({
  section: { fields: { buildingBlock: header, footer, references: questionsAndAnswers } = {} },
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const footerBuildingBlock = footer as IBuildingBlock;
  const shouldShowCategoryBar = !!header?.fields.withCheckmarks;
  const categoryBarRef = useRef<HTMLDivElement>(null);
  // This value will only change on re-render and not when the width of the window changes. It is fine for this use-case,
  // but good to be aware of if we want to somehow expand it in the future
  const categoryBarIsScrollable =
    categoryBarRef.current &&
    categoryBarRef.current.scrollWidth > categoryBarRef.current.clientWidth;

  const categories = useMemo<string[]>(() => {
    if (!shouldShowCategoryBar || !questionsAndAnswers || questionsAndAnswers.length === 0) {
      return [];
    }
    const uniqueSortedCategories = questionsAndAnswers
      .flatMap((item) => item.fields.categories as string)
      .map((item) => item.trim())
      .filter((value, index, self) => !!value && self.indexOf(value) === index)
      .sort();
    return ['all'].concat(uniqueSortedCategories);
  }, [questionsAndAnswers, shouldShowCategoryBar]);

  const filteredQuestionsAndAnswers = useMemo(() => {
    if (!questionsAndAnswers || questionsAndAnswers.length === 0) {
      return [];
    }
    if (!shouldShowCategoryBar || selectedCategory === 'all') {
      return questionsAndAnswers;
    }
    return questionsAndAnswers.filter((item) => {
      const categories = item.fields.categories as string[];
      return categories.includes(selectedCategory);
    });
  }, [questionsAndAnswers, selectedCategory, shouldShowCategoryBar]);

  return (
    <MainWrapper>
      <Col mobile="1 / span 12" tablet="3 / span 8" desktop="3 / span 8">
        {header && (
          <div css={{ marginTop: spacing[6] }}>
            {header.fields.title && (
              <div>
                <RichText document={header.fields.title} />
              </div>
            )}
            {header.fields.description && (
              <div css={{ marginTop: spacing[1] }}>
                <RichText document={header.fields.description} />
              </div>
            )}
          </div>
        )}
        {categories && header?.fields.withCheckmarks && (
          <CategoryWrapper ref={categoryBarRef}>
            {categories.map((item) => (
              <Tag
                role="button"
                key={item}
                onClick={() => {
                  let newlySelectedCategory = item;
                  if (selectedCategory === item) {
                    newlySelectedCategory = 'all';
                  }
                  setSelectedCategory(newlySelectedCategory);
                }}
                css={{
                  backgroundColor: item === selectedCategory ? colors.black : colors.lightGray,
                }}
              >
                <Typography color={item === selectedCategory ? colors.white : colors.black}>
                  {setFirstCharToUpperCase(item)}
                </Typography>
              </Tag>
            ))}
            {/* Right-margin hack since overflowing in y-direction doesn't allow for right-margin */}
            <div css={{ width: 20, color: colors.white }}>___</div>
            <MoreArrow
              isScrollable={categoryBarIsScrollable ?? true}
              onClick={() => {
                if (categoryBarRef.current) {
                  categoryBarRef.current.scrollBy({ left: 400, behavior: 'smooth' });
                }
              }}
            >
              <Icon name="chevron-right" size={36} css={{ marginRight: -10 }} />
            </MoreArrow>
          </CategoryWrapper>
        )}
        {filteredQuestionsAndAnswers && (
          <div css={{ marginTop: spacing[6] - spacing[1] }}>
            {filteredQuestionsAndAnswers.map((item) => (
              <QuestionAndAnswer
                key={item.fields.title as string}
                content={<Typography variant="bodyBold">{item.fields.title as string}</Typography>}
                expandedContent={
                  <RichText
                    css={{ marginTop: spacing[3] }}
                    document={item.fields.description as Document}
                  />
                }
                css={{ marginTop: spacing[3] }}
              />
            ))}
          </div>
        )}
        {footerBuildingBlock && (
          <FooterWrapper css={{ marginTop: spacing[6] }}>
            {footerBuildingBlock.fields.title && (
              <div>
                <RichText document={footerBuildingBlock.fields.title} />
              </div>
            )}
            {footerBuildingBlock.fields.description && (
              <div css={{ marginTop: spacing[1] }}>
                <RichText document={footerBuildingBlock.fields.description} />
              </div>
            )}
          </FooterWrapper>
        )}
      </Col>
    </MainWrapper>
  );
};

export default QuestionsAndAnswersSection;
